import type { AccountStreaks } from "@decibeltrade/sdk";

interface StreakDay {
  date: number;
  traded: boolean;
  isCurrentMonth: boolean;
  isUpcoming?: boolean;
}

export interface TransformedStreaksData {
  currentStreak: string;
  daysTraded: number;
  streakPoints: number;
  month: string;
  year: string;
  calendarDays: StreakDay[][];
  weeklyStatus: Array<"complete" | "incomplete" | "pending">;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatStreakDuration(days: number): string {
  if (days === 0) return "0 Days";
  if (days === 1) return "1 Day";

  const weeks = Math.floor(days / 7);
  const remainingDays = days % 7;

  if (remainingDays === 0) {
    return weeks === 1 ? "1 Week" : `${weeks} Weeks`;
  }

  return `${days} Days`;
}

/**
 * Build a Mon–Sun calendar grid for the given month/year.
 * Each week is an array of 7 StreakDay objects.
 */
function buildCalendarGrid(
  year: number,
  month: number,
  tradedDays: Set<number>,
  prevMonthTradedDays: Set<number>,
  nextMonthTradedDays: Set<number>,
  today: Date,
): StreakDay[][] {
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDate = today.getDate();
  const isCurrentMonth = year === currentYear && month === currentMonth;

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Day of week for the 1st (0=Sun, convert to Mon=0 ... Sun=6)
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  // Days in previous month (for overflow)
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const days: StreakDay[] = [];

  // Previous month overflow
  for (let i = startOffset - 1; i >= 0; i--) {
    const dayNum = daysInPrevMonth - i;
    days.push({
      date: dayNum,
      traded: prevMonthTradedDays.has(dayNum),
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const isUpcoming = isCurrentMonth && d > currentDate;
    days.push({
      date: d,
      traded: tradedDays.has(d),
      isCurrentMonth: true,
      ...(isUpcoming && { isUpcoming: true }),
    });
  }

  // Next month overflow to fill final week
  const remainder = days.length % 7;
  if (remainder > 0) {
    const overflow = 7 - remainder;
    for (let d = 1; d <= overflow; d++) {
      days.push({
        date: d,
        traded: nextMonthTradedDays.has(d),
        isCurrentMonth: false,
      });
    }
  }

  // Group into weeks of 7
  const weeks: StreakDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return weeks;
}

function computeWeeklyStatus(
  weeks: StreakDay[][],
  today: Date,
  year: number,
  month: number,
): Array<"complete" | "incomplete" | "pending"> {
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDate = today.getDate();
  const isCurrentMonth = year === currentYear && month === currentMonth;

  return weeks.map((week) => {
    const currentMonthDays = week.filter((d) => d.isCurrentMonth);
    if (currentMonthDays.length === 0) return "pending";

    const hasTradedDay = currentMonthDays.some((d) => d.traded);
    if (hasTradedDay) return "complete";

    if (isCurrentMonth) {
      // If all current-month days in this week are in the past → incomplete
      const allPast = currentMonthDays.every((d) => d.date < currentDate);
      if (allPast) return "incomplete";
      return "pending";
    }

    // Past months: no traded days → incomplete
    return "incomplete";
  });
}

export function transformStreaksData(
  data: AccountStreaks | undefined,
  targetYear?: number,
  targetMonth?: number,
): TransformedStreaksData {
  const now = new Date();
  const year = targetYear ?? now.getFullYear();
  const month = targetMonth ?? now.getMonth(); // 0-indexed
  const monthName = MONTH_NAMES[month] ?? "January";
  const yearStr = String(year);
  const emptySet = new Set<number>();

  if (!data) {
    const emptyGrid = buildCalendarGrid(year, month, emptySet, emptySet, emptySet, now);
    return {
      currentStreak: "0 Days",
      daysTraded: 0,
      streakPoints: 0,
      month: monthName,
      year: yearStr,
      calendarDays: emptyGrid,
      weeklyStatus: computeWeeklyStatus(emptyGrid, now, year, month),
    };
  }

  // Parse ALL qualifying dates grouped by year-month
  const qualifyingByMonth = new Map<string, Set<number>>();
  for (const dateStr of data.qualifyingDates) {
    const parts = dateStr.split("-");
    const y = Number(parts[0]);
    const m = Number(parts[1]) - 1; // convert to 0-indexed
    const d = Number(parts[2]);
    const key = `${y}-${m}`;
    const existing = qualifyingByMonth.get(key);
    if (existing) {
      existing.add(d);
    } else {
      qualifyingByMonth.set(key, new Set([d]));
    }
  }

  const tradedDaysInMonth = qualifyingByMonth.get(`${year}-${month}`) ?? emptySet;
  const daysTraded = tradedDaysInMonth.size;

  // Previous month qualifying dates (for overflow cells)
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevMonthTradedDays = qualifyingByMonth.get(`${prevYear}-${prevMonth}`) ?? emptySet;

  // Next month qualifying dates (for overflow cells)
  const nextYear = month === 11 ? year + 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextMonthTradedDays = qualifyingByMonth.get(`${nextYear}-${nextMonth}`) ?? emptySet;

  const calendarDays = buildCalendarGrid(
    year,
    month,
    tradedDaysInMonth,
    prevMonthTradedDays,
    nextMonthTradedDays,
    now,
  );

  return {
    currentStreak: formatStreakDuration(data.currentStreak),
    daysTraded,
    streakPoints: data.streakIpoints,
    month: monthName,
    year: yearStr,
    calendarDays,
    weeklyStatus: computeWeeklyStatus(calendarDays, now, year, month),
  };
}
